import { makeK8sCreator, k8sRequest } from "../api";
import { STRATEGIC_MERGE_PATCH } from "./revisions";

/**
 * Editing a workload that is already running.
 *
 * Deliberately narrow. A DaemonSet's selector and the pod labels that must
 * match it cannot be changed after creation - the API server answers 422 with
 * a message no operator should have to read - so the form does not offer them.
 * What it does offer is the two things people actually change: which image to
 * run, and how fast to roll it out.
 *
 * Anything else - command, args, environment, volumes, or where the workload is
 * targeted - still needs `kubectl`. That is a smaller feature than it could be,
 * and a much easier one to be sure about.
 */

export interface EditWorkloadForm {
    readonly image: string;
    readonly maxUnavailable: string;
}

/** Recorded so the workload's History tab says who changed it and why. */
export const CHANGE_CAUSE = "kubernetes.io/change-cause";
export const PORTAL_CHANGE_CAUSE = "Updated in the Izuma Device Management Portal";

export const validateEdit = (form: EditWorkloadForm, current: EditWorkloadForm): string[] => {
    const problems: string[] = [];

    if (!form.image.trim()) {
        problems.push("An image is required.");
    }

    const nodes = parseInt(form.maxUnavailable, 10);
    if (!/^\d+$/.test(form.maxUnavailable.trim()) || nodes < 1) {
        problems.push("Nodes updated at a time must be a whole number of 1 or more.");
    }

    if (form.image.trim() === current.image && form.maxUnavailable.trim() === current.maxUnavailable) {
        problems.push("Nothing has changed.");
    }

    return problems;
};

export interface UpdatePatch {
    readonly metadata: { readonly annotations: { [key: string]: string } };
    readonly spec: unknown;
}

/**
 * Only what changed is sent.
 *
 * The image goes inside a container named the same as the running one, because
 * strategic merge matches list entries by name - without it the patch would
 * replace the container list rather than edit the one container.
 *
 * maxUnavailable lives in updateStrategy rather than the pod template, so
 * changing it alone does not produce a new revision.
 */
export const buildUpdatePatch = (
    form: EditWorkloadForm,
    current: EditWorkloadForm,
    containerName: string
): UpdatePatch => {
    const image = form.image.trim();
    const maxUnavailable = form.maxUnavailable.trim();

    const spec: { template?: unknown; updateStrategy?: unknown } = {};

    if (image !== current.image) {
        spec.template = { spec: { containers: [{ name: containerName, image }] } };
    }

    if (maxUnavailable !== current.maxUnavailable) {
        spec.updateStrategy = { rollingUpdate: { maxUnavailable: parseInt(maxUnavailable, 10) } };
    }

    return { metadata: { annotations: { [CHANGE_CAUSE]: PORTAL_CHANGE_CAUSE } }, spec };
};

const updated = makeK8sCreator<unknown, UpdatePatch>("K8S_WORKLOAD_UPDATE", (body) => body);

/** The request options for an update, separated out so they can be asserted. */
export const updateRequest = (name: string, patch: UpdatePatch, namespace = "default") => ({
    ...k8sRequest(`apis/apps/v1/namespaces/${encodeURIComponent(namespace)}/daemonsets/${encodeURIComponent(name)}`),
    method: "PATCH" as const,
    // `type`, not a Content-Type header - see the note on the rollback request.
    type: STRATEGIC_MERGE_PATCH,
    data: patch,
});

const updateWorkloadAction = (name: string, patch: UpdatePatch, namespace = "default") =>
    updated.createThunk(updateRequest(name, patch, namespace));

const resetUpdateAction = () => updated.createResetAction();

export { updateWorkloadAction, resetUpdateAction };
export const updateReducer = updated.reducer;
